import React, { useEffect, useState } from 'react';
import { MdOutlineKeyboardArrowDown, MdOutlineKeyboardArrowUp } from 'react-icons/md';
import { FaSearch, FaTimesCircle } from 'react-icons/fa';
import Label from 'models/LabelModel';
import axios from 'helpers/axios';
import config from 'helpers/config';

interface Props {
    onchange: (tags: Label[]) => void;
}

const TagsPicker = (props: Props) => {
    const [isTagsPickerOpen, setIsTagsPickerOpen] = useState<boolean>(false);
    const [RO_tags, setRO_tags] = useState<Label[]>([]);
    const [selectedTags, setSelectedTags] = useState<Label[]>([]);
    const [searchTerm, setSearchTerm] = useState<string>('');

    const filteredTags = RO_tags.filter((tag) => tag.title.toLowerCase().includes(searchTerm.toLowerCase()));

    const clearAllTags = () => {
        setSelectedTags([]);
    };

    const toggleVisibility = () => {
        if (RO_tags.length === 0) {
            axios.get(`${config.apiUrl}/label/order`).then((res) => {
                setRO_tags(res.data);
            });
        }
        setIsTagsPickerOpen(!isTagsPickerOpen);
    };

    const handleClickOutside = (event: MouseEvent) => {
        const target = event.target as HTMLElement;
        const dropdown = document.getElementById('tagsPicker');
        const button = document.getElementById('tagsPickerButton');

        if (dropdown && !dropdown.contains(target) && button && !button.contains(target)) {
            setIsTagsPickerOpen(false);
        }
    };

    useEffect(() => {
        if (isTagsPickerOpen) {
            document.addEventListener('click', handleClickOutside);
        } else {
            document.removeEventListener('click', handleClickOutside);
        }

        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, [isTagsPickerOpen]);

    useEffect(() => {
        props.onchange(selectedTags);
    }, [selectedTags]);

    return (
        <div className="flex">
            <div className="relative">
                <button
                    id="tagsPickerButton"
                    className={`mr-1.5 border border-gray-300 rounded-lg py-2 px-4 font-medium bg-white hover:bg-gray-50 transition-colors duration-150 flex items-center space-x-2 ${
                        !!isTagsPickerOpen && 'border-blue-500 ring-2 ring-blue-200'
                    }`}
                    onClick={() => toggleVisibility()}
                >
                    <span className="text-black">Tags</span>
                    {selectedTags.length > 0 && (
                        <span className="bg-blue-600 text-white text-xs px-2 py-0.5 rounded-full">
                            {selectedTags.length}
                        </span>
                    )}
                    <span className="text-gray-500">
                        {isTagsPickerOpen ? <MdOutlineKeyboardArrowUp /> : <MdOutlineKeyboardArrowDown />}
                    </span>
                </button>

                {!!isTagsPickerOpen && (
                    <div
                        className="absolute bg-white border border-gray-200 rounded-lg shadow-lg w-80 z-50 mt-2"
                        id={`tagsPicker`}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Header */}
                        <div className="flex items-center justify-between p-4 border-b border-gray-200">
                            <h3 className="font-semibold text-gray-900">Filter by Tags</h3>
                            <button
                                onClick={clearAllTags}
                                className="text-sm text-red-600 hover:text-red-800 font-medium"
                            >
                                Clear all
                            </button>
                        </div>

                        {/* Search Input */}
                        <div className="p-4 border-b border-gray-200">
                            <div className="relative">
                                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                                <input
                                    type="text"
                                    placeholder="Search tags..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>
                        </div>

                        {/* Tags List */}
                        <div className="max-h-64 overflow-y-auto">
                            {filteredTags.map((tag) => {
                                const isSelected = selectedTags.some((selectedTag) => selectedTag.id === tag.id);
                                return (
                                    <div
                                        key={tag.id}
                                        className="flex items-center p-3 hover:bg-gray-50 cursor-pointer transition-colors duration-150"
                                        onClick={() => {
                                            if (isSelected) {
                                                setSelectedTags(selectedTags.filter((t) => t.id !== tag.id));
                                            } else {
                                                setSelectedTags([...selectedTags, tag]);
                                            }
                                        }}
                                    >
                                        {/* Checkbox */}
                                        <div className="flex items-center mr-3">
                                            <input
                                                type="checkbox"
                                                checked={isSelected}
                                                onChange={() => {}} // Handled by parent onClick
                                                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                            />
                                        </div>

                                        {/* Color Circle */}
                                        <div
                                            className="w-3 h-3 rounded-full mr-3 flex-shrink-0"
                                            style={{ backgroundColor: tag.color || '#6B7280' }}
                                        ></div>

                                        {/* Tag Name */}
                                        <span className="font-medium text-gray-900 flex-1">{tag.title}</span>
                                    </div>
                                );
                            })}

                            {filteredTags.length === 0 && searchTerm && (
                                <div className="p-4 text-center text-gray-500">
                                    No tags found matching "{searchTerm}"
                                </div>
                            )}
                        </div>

                        {/* Footer */}
                        <div className="flex items-center justify-between p-4 border-t border-gray-200 bg-gray-50">
                            <span className="text-sm text-gray-600">
                                {selectedTags.length} of {RO_tags.length} tags selected
                            </span>
                            <button
                                onClick={() => setIsTagsPickerOpen(false)}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-150 font-medium"
                            >
                                Done
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {selectedTags?.map((tag) => (
                // <span
                //     key={tag.id}
                //     style={{ backgroundColor: tag.color }}
                //     className="mr-2 px-4 py-1 rounded-3xl hover:opacity-80 text-xs"
                // >
                //     <span className="font-semibold text-white inline-flex whitespace-nowrap pt-1.5 ">
                //         <FaTimesCircle
                //             onClick={() => {
                //                 setSelectedTags(selectedTags.filter((t) => t !== tag));
                //                 // props.onchange(selectedTags);
                //             }}
                //             className="inline mr-1 clickable align-baseline text-xs"
                //         />
                //         {tag.title}
                //     </span>
                // </span>
                <span
                    key={tag.id}
                    className="mr-2 inline-flex items-center px-3 py-1 text-white rounded-full text-sm font-medium bg-gray-100  hover:bg-gray-200 transition-colors duration-150"
                    style={{ backgroundColor: tag.color }}
                >
                    <div
                        className="w-2 h-2 rounded-full mr-2"
                        style={{ backgroundColor: tag.color || '#6B7280' }}
                    ></div>
                    <span className="mr-1">{tag.title}</span>
                    <button
                        onClick={() => {
                            setSelectedTags(selectedTags.filter((t) => t.id !== tag.id));
                        }}
                        className="ml-1 text-gray-500 hover:text-gray-700 focus:outline-none"
                    >
                        <FaTimesCircle className="w-3 h-3 text-white" />
                    </button>
                </span>
            ))}
        </div>
    );
};

export default TagsPicker;
